import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ConversationItem } from '@vivport/backend/types';
import { Button } from '@/components/ui/button';
import { CheckCircle2 } from 'lucide-react';
import { getIntegrationKind } from '@/pages/settings/integrations/integration-kinds';
import { startConnectInNewTab } from '@/pages/settings/integrations/start-connect-in-new-tab';
import {
  readableReason,
  subscribeToConnectBroadcast,
} from '@/pages/settings/integrations/useIntegrationConnectBroadcasts';
import { useOauthConnectStarters } from '@/pages/settings/integrations/useOauthConnectStarters';

type ExternalConnectOfferItem = Extract<ConversationItem, { kind: 'external_connect_offer' }>;

export interface ProvenanceExternalConnectCardProps {
  item: ExternalConnectOfferItem;
}

type CardState = { status: 'ready' } | { status: 'connected' } | { status: 'failed'; reason: string };

export function ProvenanceExternalConnectCard({ item }: ProvenanceExternalConnectCardProps) {
  const queryClient = useQueryClient();
  const starters = useOauthConnectStarters();
  const definition = getIntegrationKind(item.integrationKind);
  const [state, setState] = useState<CardState>({ status: 'ready' });

  useEffect(
    () =>
      subscribeToConnectBroadcast(item.integrationKind, queryClient, (result) => {
        setState(
          result.status === 'success'
            ? { status: 'connected' }
            : { status: 'failed', reason: readableReason(result.reason) }
        );
      }),
    [item.integrationKind, queryClient]
  );

  return (
    <div className="w-full rounded-lg border border-border p-3 text-sm text-foreground">
      <div className="flex items-center gap-2">
        <img src={definition.logo} alt="" className="h-5 w-5 shrink-0 object-contain" />
        <p className="font-medium">{item.label}</p>
        {state.status === 'connected' ? (
          <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-success" />
            Connected
          </span>
        ) : null}
      </div>
      {state.status === 'connected' ? null : (
        <>
          <Button
            type="button"
            size="sm"
            className="mt-3"
            onClick={() => startConnectInNewTab({ label: item.label, start: starters[item.integrationKind] })}
          >
            Connect {item.label}
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            Opens {definition.vendor} in a new tab. Nothing is connected until you finish there.
          </p>
          {state.status === 'failed' ? (
            <p className="mt-1 text-xs text-destructive">The connection did not finish: {state.reason}.</p>
          ) : null}
        </>
      )}
    </div>
  );
}
